import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  PromptInput,
  PromptInputAction,
  PromptInputActions,
  PromptInputTextarea,
} from "@arcadeai/design-system/components/ui/molecules/prompt-input";
import { cn } from "@arcadeai/design-system/lib/utils";
import { ArrowUpIcon, SquareIcon } from "lucide-react";
import type { ReactNode } from "react";

export type ChatInputProps = {
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  isLoadingTools?: boolean;
  /** Hard-disable the composer, e.g. when no chat model is configured. */
  disabled?: boolean;
  inputLeadingAction?: ReactNode;
  placeholder?: string;
  autoFocus?: boolean;
  value: string;
  onValueChange: (value: string) => void;
};

export function ChatInput({
  isStreaming,
  onSend,
  onStop,
  isLoadingTools = false,
  disabled = false,
  inputLeadingAction,
  placeholder = "Send a message...",
  autoFocus = false,
  value,
  onValueChange,
}: ChatInputProps) {
  function handleSubmit() {
    if (disabled) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    onSend(trimmed);
    onValueChange("");
  }

  return (
    <PromptInput
      disabled={disabled || isLoadingTools}
      isLoading={isStreaming}
      onSubmit={handleSubmit}
      onValueChange={onValueChange}
      value={value}
    >
      <PromptInputTextarea autoFocus={autoFocus} placeholder={placeholder} />
      <PromptInputActions
        className={cn(
          "px-2 pb-2",
          inputLeadingAction ? "justify-between" : "justify-end"
        )}
      >
        {inputLeadingAction}
        <PromptInputAction
          tooltip={isStreaming ? "Stop generating" : "Send message"}
        >
          <Button
            aria-label={isStreaming ? "Stop generating" : "Send message"}
            disabled={
              isStreaming ? false : disabled || isLoadingTools || !value.trim()
            }
            onClick={isStreaming ? onStop : handleSubmit}
            size="icon"
            variant={isStreaming ? "destructive" : "default"}
          >
            {isStreaming ? (
              <SquareIcon className="size-4 fill-current" />
            ) : (
              <ArrowUpIcon className="size-4" />
            )}
          </Button>
        </PromptInputAction>
      </PromptInputActions>
    </PromptInput>
  );
}
